<template>
  <v-row class="mt-2">
    <v-col class="py-0" cols="12">
      <v-alert tile type="error" v-if="error">{{ error }}</v-alert>
    </v-col>
    <v-col cols="12" md="2" sm="6">
      <v-text-field
        :label="$t('label.search')"
        @click:append-outer="refresh()"
        @click:clear="clearSearch()"
        @keydown.enter="refresh()"
        clearable
        dense
        hide-details
        outlined
        required
        v-model="search"
      ></v-text-field>
    </v-col>
    <v-spacer />
    <v-col align="right" cols="12" md="2" sm="6">
      <v-btn @click="resetFilters()" color="primary" dark rounded small text>
        {{ $t("label.clearFilters") }}
      </v-btn>
    </v-col>
    <v-col cols="12" md="12">
      <v-data-table
        :footer-props="{
          'disable-pagination': $apollo.queries.allPartners.loading,
          'items-per-page-options': [10, 25, 50, 75, 100],
        }"
        :headers="headers"
        :items="allPartners.edges"
        :loading="$apollo.queries.allPartners.loading"
        :options.sync="dataTableOptions"
        :server-items-length="allPartners.totalCount"
        :show-select="me.isSuperuser ? 'show-select' : undefined"
        item-key="node.id"
        v-model="selectedItems"
      >
        <template v-slot:[`item.action`]="{ item }">
          <v-btn @click="edit(item.node)" color="primary" icon>
            <v-icon>edit</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-col>

    <DeleteDialog
      :dialog.sync="deleteDialog"
      v-on:confirmed="onDeleteConfirmed"
    />
    <PartnerEditDialog
      :dialog.sync="editDialog"
      :object.sync="editedItem"
      v-on:changed="onChanged"
    />
  </v-row>
</template>

<script>
import DeleteDialog from "@/components/base/DeleteDialog.vue";
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import PartnerEditDialog from "@/components/users/PartnerEditDialog.vue";

export default {
  name: "partners-list",
  components: {
    DeleteDialog,
    PartnerEditDialog,
  },

  apollo: {
    allPartners: {
      query: gql`
        query allPartners(
          $first: Int
          $last: Int
          $before: String
          $after: String
          $orderBy: [String]
          $search: String
        ) {
          allPartners(
            first: $first
            last: $last
            before: $before
            after: $after
            search: $search
            orderBy: $orderBy
          ) {
            edgeCount
            totalCount
            pageInfo {
              startCursor
              endCursor
              hasPreviousPage
              hasNextPage
            }
            edges {
              node {
                id
                name
                tenants {
                  edges {
                    node {
                      id
                      tenantName
                    }
                  }
                  totalCount
                }
              }
            }
          }
        }
      `,
      fetchPolicy: "cache-and-network",
      update: (data) => data.allPartners,
      skip: true,
      pollInterval: 60000,
    },
  },

  data: function () {
    return {
      allPartners: {},
      deleteDialog: false,
      editDialog: false,
      editedItem: {},
      error: null,
      headers: [
        {
          text: this.$tc("label.name", 1),
          value: "node.name",
          sortable: true,
        },
        {
          text: this.$tc("label.quantity", 1),
          value: "node.tenants.totalCount",
          sortable: true,
        },
        {
          text: this.$t("label.action"),
          align: "center",
          value: "action",
          sortable: false,
        },
      ],
      selectedItems: [],
    };
  },

  computed: {
    dataTableOptions: {
      get() {
        return this.$store.getters["partnersList/getDataTableOptions"];
      },
      set(value) {
        this.$store.dispatch("partnersList/setDataTableOptions", value);
      },
    },

    me() {
      return this.$store.state.user.me;
    },

    search: {
      get() {
        return this.$store.getters["partnersList/getSearch"];
      },
      set(value) {
        this.$store.dispatch("partnersList/setSearch", value);
      },
    },
  },

  watch: {
    dataTableOptions() {
      this.refresh();
    },

    selectedItems() {
      this.$emit("updateNrOfSelectedItems", this.selectedItems.length);
    },
  },

  created() {
    this.orderByList = helper.orderByList;
  },

  mounted() {},

  methods: {
    add() {
      this.editedItem = null;
      this.editDialog = true;
    },

    clearSearch() {
      this.search = null;
      this.refresh();
    },

    deleteSelected() {
      this.deleteDialog = true;
    },

    edit(item) {
      this.editedItem = item;
      this.editDialog = true;
    },

    onChanged() {
      this.$apollo.queries.allPartners.refresh();
    },

    onDeleteConfirmed() {
      var ids = [];
      this.selectedItems.forEach(function (item, index) {
        ids.push(item.node.id);
      });

      const payload = {
        ids: ids,
      };

      this.$apollo
        .mutate({
          mutation: gql`
            mutation deletePartners($input: DeletePartnersInput!) {
              deletePartners(input: $input) {
                deletionCount
              }
            }
          `,
          variables: {
            input: payload,
          },
        })
        .then(() => {
          this.$apollo.queries.allPartners.refresh();

          this.deleteDialog = false;
          this.selectedItems = [];

          const payload = {
            color: "success",
            message: `Partners successfully deleted`,
          };
          this.$store.dispatch("snackbar/showMessage", payload);
        })
        .catch((error) => {
          this.error = error;
        });
    },

    refresh() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.dataTableOptions;

      var _first = itemsPerPage;
      var _last = null;
      var _before = null;
      var _after = null;

      if (page < this.page) {
        _first = null;
        _last = itemsPerPage;
        _before = this.allPartners.pageInfo.startCursor;
        _after = null;
      }

      if (page > this.page) {
        _first = itemsPerPage;
        _last = null;
        _before = null;
        _after = this.allPartners.pageInfo.endCursor;
      }

      var orderByList = this.orderByList(sortBy, sortDesc);

      this.$apollo.queries.allPartners.setVariables({
        first: _first,
        last: _last,
        before: _before,
        after: _after,
        orderBy: orderByList,
        search: this.search,
      });

      this.$apollo.queries.allPartners.skip = false;
      this.$apollo.queries.allPartners.refresh();

      this.page = page;
    },

    resetFilters() {
      this.clearSearch();
    },
  },
};
</script>
